import { CommonModule } from '@angular/common';
import { Component, OnInit, computed, effect, inject, input, signal } from '@angular/core';
import { Router } from '@angular/router';
import { ProductDetailOverlayComponent } from 'src/app/category/components/product-detail-overlay/product-detail-overlay.component';
import { BrowserApiService } from 'src/app/core/services/browser-api/browser-api.service';
import { OverlayService } from 'src/app/core/services/overlay/overlay.service';
import { ProductListing, ProductListingVariant } from 'src/app/products/models/product-listing.model';
import { ProductDealCountdown } from 'src/app/products/models/product.model';
import { DealCountdownService } from 'src/app/products/services/deal-countdown.service';
import { PriceService } from 'src/app/products/services/price.service';
import { VariantAttribute } from '../../enum/category-page.enum';
import { ColorVariant } from '../../interfaces/category-page.interface';

@Component({
  standalone: true,
  selector: 'webstore-product-varient-management',
  template: ``,
  providers: [DealCountdownService],
  imports: [CommonModule]
})
export class ProductVariantManagementComponent implements OnInit {
  protected priceService = inject(PriceService);
  protected router = inject(Router);
  protected overlayService = inject(OverlayService);
  protected browserApiService = inject(BrowserApiService);
  protected dealCountdownService = inject(DealCountdownService);

  productVariant: ProductListingVariant = {
    id: '',
    name: '',
    sku: '',
    brand: { name: '' },
    mrp: 0,
    tags: [],
    in_stock: false,
    can_order: false,
    is_exposed: false,
    one_line_description: '',
    featured_images: [],
    price_list: {
      other_offers: [],
      recommended_offers: {
        applicable_amount: 0,
        buyer_groups: [],
        sales_channel: [],
        applied_offer_type: '',
        discount_rate: 0,
        discount_type: '',
        max_allowed_quantity: 0,
        max_allowed_inventory: 0,
        maximum_offer_amount_per_transaction: 0,
        minimum_quantity: 0,
        offer_id: '',
        offer_name: '',
        offer_type: '',
        percent_savings: 0,
        savings: 0,
        description: '',
        terms: '',
        start_date: '',
        end_date: '',
        bundle_item_quantity: 0,
        bundle_item: undefined,
        cash_points: 0
      }
    },
    is_active: false,
    variant_values: [],
    sales_channel: [],
    selling_price: 0,
    is_archived: false,
    is_disabled: false,
    is_visible: false,
    is_allow_back_orders: false,
    max_quantity_per_transaction: 0,
    seo_detail: {
      keywords: [],
      meta_title: '',
      meta_description: '',
      also_known_as_names: []
    },
    url_path: '',
    display_name: '',
    is_grouped_product: false,
    deal: null,
    price_for_everyone: 0
  };
  isVariantAvailable = false;
  allAvailableProductVariants: ProductListingVariant[] = [];

  productDealCountDown!: ProductDealCountdown;
  // Convert Observable to computed signal
  readonly remainingTime = computed(() => this.dealCountdownService.remainingTime());
  isDealAvailable = false;

  readonly productInput = input<ProductListing>({} as ProductListing);
  private _product = signal<ProductListing>({} as ProductListing);

  // Computed getter for backward compatibility
  get product() {
    return this._product();
  }

  showImages = false;
  previewImage = '';
  colorVariants: ColorVariant[] = [];
  _sellingPrice = 0;
  _actualPrice = 0;

  constructor() {
    // Effect to handle productInput changes
    effect(() => {
      const product = this.productInput();
      if (product) {
        if (product?.variants.length > 0) {
          this.allAvailableProductVariants = product.variants?.filter(product => product.is_active && product.is_visible);
          const firstVariant = this.allAvailableProductVariants[0];
          if (firstVariant) {
            this.productVariant = firstVariant;
          }

          this.isDealAvailable = !!this.productVariant?.deal;
          this.productDealCountDown = this.productVariant?.deal;

          // create the variants array including the current product details
          const allProductVariants = [...product?.variants ?? []];

          // process only on input change
          this.colorVariants = this.getColorVariants(allProductVariants);
          // Checking is mrp present in all variants
          const priceAvailableInAllVariants = allProductVariants?.every(variant => variant?.hasOwnProperty('mrp'));
          // if yes then applying sorting and setting lowest price variant as default variant
          if (priceAvailableInAllVariants && allProductVariants.length > 0) {
            // getting variant-group details
            const variant_group = product?.variant_group;
            // Getting active and visible to custom variants
            const variantsForSale = allProductVariants.filter(variant => variant?.is_active && variant?.is_visible);
            // As response is sorted the first variant is having lowest price 
            const lowestPriceVariant = variantsForSale[0];
            // Assigning lowest price variant product as default product 
            const updatedProduct = { ...lowestPriceVariant, name: lowestPriceVariant?.display_name, featured_images: [{ url: lowestPriceVariant?.featured_images?.[0]?.url }], variant_group } as unknown as ProductListing;
            this._product.set(updatedProduct);
          }
        }

        this._product.set(product);
        const { actualPrice, sellingPrice } = this.priceService.getProductListingPrice(this.productVariant);
        this._sellingPrice = sellingPrice;
        this._actualPrice = actualPrice;
        this.isVariantAvailable = this.allAvailableProductVariants?.some(variant => variant?.variant_values?.length > 0);

        // Initialize countdown timer if deal is available
        if (this.isDealAvailable && this.productDealCountDown?.end_time) {
          const endDay = new Date(this.productDealCountDown.end_time);
          this.dealCountdownService.getCountDown(endDay);
        }

        // set initial image for preview
        this.setDefaultImage();
      }
    });
  }

  ngOnInit(): void {
    // Deal countdown initialization is now handled in the constructor effect
  }

  private setDefaultImage(): void {
    this.previewImage = this.productVariant?.featured_images?.[0]?.url || '';
  }

  setPreviewImage(imageUrl: string): void {
    this.previewImage = imageUrl;
  }

  clearPreviewImage(): void {
    this.setDefaultImage();
  }

  private getColorVariants(variants: ProductListingVariant[]): ColorVariant[] {
    // filter out variants which cannot be sold
    const variantsForSale = variants.filter(variant => variant?.is_active && variant?.is_visible);

    const hasColorsForAll = variantsForSale.every(variant => Array.isArray(variant?.variant_values) && variant.variant_values.some(attribute => attribute.key === VariantAttribute.Color));
    if (!hasColorsForAll) {
      return [];
    }

    if (Array.isArray(variantsForSale)) {
      const colorVariants = variantsForSale
        .map(variant => {
          const color = variant.variant_values.find(attribute => attribute.key === VariantAttribute.Color)?.value;
          return ({
            color,
            image: variant.featured_images[0]?.url,
            url_path: variant.url_path,
            product_id: variant.id,
            in_stock: variant?.in_stock
          });
        })
        .sort((v1, v2) => {
          if (v1.color === v2.color) {
            return v1.in_stock ? -1 : 1;
          }
          return (v1.color || '') > (v2.color || '') ? 1 : -1;
        });

      const uniqueColors = [...new Set(colorVariants.map(colorVariant => colorVariant?.color))];
      const uniqueColorVariants = uniqueColors.map(color => colorVariants.find(variant => variant.color === color));

      return uniqueColorVariants.filter(Boolean) as ColorVariant[];
    }
    return [];
  }

  openProductDetailOverlay(): void {
    this.overlayService.open(ProductDetailOverlayComponent, this.productVariant.url_path, true, 'centered', 'product-detail-overlay', 'block-scroll');
  }

  navigateToProductDetailPage(route: string): void {
    const url = this.router.serializeUrl(this.router.createUrlTree([route]));
    this.browserApiService.open(url, '_blank');
  }
}
